import { useState, useMemo, useCallback, useEffect } from 'react';

interface Range {
    start: number;
    end: number;
}

export interface PaginationState {
    currentPage: number;
    pageSize: number;
    displayedEntries: Range;
    totalEntries: number;
    numberOfPages: number;
    setPage(page: number): void;
    initialized: boolean;
}

export function calculatePageRange(currentPage: number, numberOfPages: number, pagesToDisplay: number): Range {
    const lastPage = numberOfPages - 1;
    if (numberOfPages < pagesToDisplay) {
        return {
            start: 0,
            end: lastPage,
        };
    }

    let startPage = currentPage - (pagesToDisplay - 1) / 2;
    let endPage = currentPage + (pagesToDisplay - 1) / 2;

    if (startPage < 0) {
        endPage += startPage * -1;
        startPage = 0;
    }

    if (endPage > numberOfPages - 1) {
        startPage -= endPage - lastPage;
        endPage = lastPage;
    }

    return {
        start: startPage,
        end: endPage,
    };
}

export function usePagination(itemCount?: number, pageSize: number = 10): PaginationState {
    if (pageSize < 0) {
        throw new Error(`Invalid page size ${pageSize}`);
    }

    const [currentPage, setCurrentPage] = useState(0);

    // Clamp when the item count shrinks under the current page (search/filter change,
    // deletions) — otherwise server-paginated tables keep requesting an empty page.
    useEffect(() => {
        if (itemCount === undefined || itemCount === null) {
            return;
        }
        const lastPage = Math.max(0, (pageSize ? Math.ceil(itemCount / pageSize) : 1) - 1);
        if (currentPage > lastPage) {
            setCurrentPage(lastPage);
        }
    }, [itemCount, pageSize, currentPage]);

    const calculateValue = useCallback(
        function (): PaginationState {
            let itemCountLocal = itemCount;
            let initialized = true;

            if (itemCountLocal === undefined || itemCountLocal === null) {
                itemCountLocal = 0;
                initialized = false;
            }

            const numberOfPages = pageSize ? Math.ceil(itemCountLocal / pageSize) : 1;
            const goToPage = (page: number) => {
                if (page < 0) {
                    page = 0;
                }
                if (numberOfPages === 0) {
                    page = 0;
                } else if (page > numberOfPages - 1) {
                    page = numberOfPages - 1;
                }

                setCurrentPage(page);
            };

            return {
                currentPage,
                pageSize,
                displayedEntries: pageSize
                    ? { start: currentPage * pageSize, end: Math.min((currentPage + 1) * pageSize - 1, itemCountLocal) }
                    : { start: 0, end: itemCountLocal - 1 },
                numberOfPages: numberOfPages,
                totalEntries: itemCountLocal,
                setPage: goToPage,
                initialized,
            };
        },
        [itemCount, pageSize, currentPage]
    );

    return useMemo(calculateValue, [calculateValue]);
    // const [value, setValue] = useState(calculateValue());

    // useEffect(() => {
    //     setValue(calculateValue());
    // }, [calculateValue]);

    // return value;
}

export function paginateArray<T>(pagination: PaginationState, arr: T[]) {
    return arr.slice(pagination.displayedEntries.start, pagination.displayedEntries.end + 1);
}

export { default } from './Pagination';